import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface NewtonGravityArrowsProps {
  width?: number;
  height?: number;
}

export const NewtonGravityArrows: React.FC<NewtonGravityArrowsProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);
  const [mass1, setMass1] = useState(50);
  const [mass2, setMass2] = useState(10);
  
  const obj1Ref = useRef({ x: size * 0.3, y: size / 2 });
  const obj2Ref = useRef({ x: size * 0.7, y: size / 2 });
  const draggingRef = useRef<1 | 2 | null>(null);
  const canvasElementRef = useRef<HTMLCanvasElement | null>(null);

  const setCanvasRef = (el: HTMLCanvasElement | null) => {
    canvasElementRef.current = el;
  };

  useEffect(() => {
    const handleDown = (e: MouseEvent | TouchEvent) => {
      if (!canvasElementRef.current) return;
      const rect = canvasElementRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      
      const r1 = 15 + Math.sqrt(mass1) * 3;
      const dx1 = x - obj1Ref.current.x;
      const dy1 = y - obj1Ref.current.y;
      if (Math.sqrt(dx1*dx1 + dy1*dy1) < r1 + 10) {
        draggingRef.current = 1;
        return;
      }

      const r2 = 10 + Math.sqrt(mass2) * 3;
      const dx2 = x - obj2Ref.current.x;
      const dy2 = y - obj2Ref.current.y;
      if (Math.sqrt(dx2*dx2 + dy2*dy2) < r2 + 10) {
        draggingRef.current = 2;
      }
    };

    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!draggingRef.current || !canvasElementRef.current) return;
      e.preventDefault();
      const rect = canvasElementRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const x = clientX - rect.left;
      const y = clientY - rect.top;

      if (draggingRef.current === 1) {
        obj1Ref.current.x = x;
        obj1Ref.current.y = y;
      } else {
        obj2Ref.current.x = x;
        obj2Ref.current.y = y;
      }
    };

    const handleUp = () => {
      draggingRef.current = null;
    };

    const canvas = canvasElementRef.current;
    if (canvas) {
      canvas.addEventListener('mousedown', handleDown);
      window.addEventListener('mousemove', handleMove, { passive: false });
      window.addEventListener('mouseup', handleUp);
      
      canvas.addEventListener('touchstart', handleDown);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleUp);
    }

    return () => {
      if (canvas) {
        canvas.removeEventListener('mousedown', handleDown);
        canvas.removeEventListener('touchstart', handleDown);
      }
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, [mass1, mass2]);

  // Calculate Force
  const dx = obj2Ref.current.x - obj1Ref.current.x;
  const dy = obj2Ref.current.y - obj1Ref.current.y;
  const distanceSq = dx*dx + dy*dy;
  const distance = Math.sqrt(distanceSq) || 1;
  const force = (mass1 * mass2) / (distanceSq * 0.01); // Scale for visual

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number, canvas: HTMLCanvasElement) => {
    if (canvasElementRef.current !== canvas) {
      setCanvasRef(canvas);
    }

    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const r1 = 15 + Math.sqrt(mass1) * 3;
    const r2 = 10 + Math.sqrt(mass2) * 3;

    // Draw Distance Line
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(255,255,255,0.1)';
    ctx.setLineDash([4, 4]);
    ctx.moveTo(obj1Ref.current.x, obj1Ref.current.y);
    ctx.lineTo(obj2Ref.current.x, obj2Ref.current.y);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw Arrows
    const dirX = dx / distance;
    const dirY = dy / distance;
    
    // Scale arrows based on force, bounded
    const arrowLen = Math.min(distance / 2 - 10, Math.max(10, force * 5));
    const arrowWidth = Math.min(10, Math.max(1, force));

    ctx.strokeStyle = '#FDE047';
    ctx.fillStyle = '#FDE047';
    ctx.lineWidth = arrowWidth;

    // Arrow 1 (on obj1 pointing to obj2)
    const a1StartX = obj1Ref.current.x + dirX * r1;
    const a1StartY = obj1Ref.current.y + dirY * r1;
    const a1EndX = a1StartX + dirX * arrowLen;
    const a1EndY = a1StartY + dirY * arrowLen;
    
    ctx.beginPath();
    ctx.moveTo(a1StartX, a1StartY);
    ctx.lineTo(a1EndX, a1EndY);
    ctx.stroke();
    
    // Arrow 1 Head
    ctx.save();
    ctx.translate(a1EndX, a1EndY);
    ctx.rotate(Math.atan2(dirY, dirX));
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-6 - arrowWidth, -4 - arrowWidth/2);
    ctx.lineTo(-6 - arrowWidth, 4 + arrowWidth/2);
    ctx.fill();
    ctx.restore();

    // Arrow 2 (on obj2 pointing to obj1)
    const a2StartX = obj2Ref.current.x - dirX * r2;
    const a2StartY = obj2Ref.current.y - dirY * r2;
    const a2EndX = a2StartX - dirX * arrowLen;
    const a2EndY = a2StartY - dirY * arrowLen;
    
    ctx.beginPath();
    ctx.moveTo(a2StartX, a2StartY);
    ctx.lineTo(a2EndX, a2EndY);
    ctx.stroke();
    
    // Arrow 2 Head
    ctx.save();
    ctx.translate(a2EndX, a2EndY);
    ctx.rotate(Math.atan2(-dirY, -dirX));
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(-6 - arrowWidth, -4 - arrowWidth/2);
    ctx.lineTo(-6 - arrowWidth, 4 + arrowWidth/2);
    ctx.fill();
    ctx.restore();

    // Draw Objects
    ctx.fillStyle = '#4A90D9';
    ctx.beginPath();
    ctx.arc(obj1Ref.current.x, obj1Ref.current.y, r1, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#A0A0A0';
    ctx.beginPath();
    ctx.arc(obj2Ref.current.x, obj2Ref.current.y, r2, 0, Math.PI * 2);
    ctx.fill();

    if (!draggingRef.current && frameCount < 180) {
      ctx.fillStyle = `rgba(255, 255, 255, ${Math.abs(Math.sin(frameCount * 0.05)) * 0.5 + 0.2})`;
      ctx.textAlign = 'center';
      ctx.font = '12px sans-serif';
      ctx.fillText('Drag objects', size/2, 30);
    }

  }, [size, mass1, mass2, dx, dy, distance, force]);

  const canvasHookRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size - 120, position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #1E293B', cursor: draggingRef.current ? 'grabbing' : 'grab' }}>
        <canvas ref={canvasHookRef} />
      </div>
      
      <div style={{ marginTop: '16px', width: '100%', maxWidth: '300px' }}>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '12px', color: '#4A90D9' }}>Planet Mass</label>
            <input 
              type="range" min="10" max="100" value={mass1} 
              onChange={(e) => setMass1(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '12px', color: '#A0A0A0' }}>Moon Mass</label>
            <input 
              type="range" min="1" max="50" value={mass2} 
              onChange={(e) => setMass2(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        
        <div style={{ textAlign: 'center', fontSize: '14px', color: '#E2E8F0', padding: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
          <div>Force = Mass × Mass ÷ Distance²</div>
          <div style={{ color: '#FDE047', fontWeight: 'bold', marginTop: '4px', fontSize: '16px' }}>
            Force: {force.toFixed(1)} N
          </div>
        </div>
      </div>
    </div>
  );
};
